import { useContext, useMemo } from "react";
import { AvisConstants } from "../../data/avisConstants";
import useDeviceDetection, { AvisDevice } from "../../hooks/useDevice";
import { useSessionStorage } from "../../hooks/useSessionStorage";
import { PortalType } from "../../models";
import { EIdRedirectPage } from "../auth/EIdRedirectPage";
import { AvisContext } from "../shared/context/AvisContext";
import { isMobileView, isTabletOrMobileView } from "./appStates/appStates";
import { NotFoundPage } from "./error/NotFoundPage";

interface IPortalSwitchHocProps {
  authors: React.ReactNode;
  licensees: React.ReactNode;
  blankMedia: React.ReactNode;
  publicPortal: React.ReactNode;
}

export function PortalSwitchHoc(props: Readonly<IPortalSwitchHocProps>) {
  const { authors, licensees, blankMedia, publicPortal } = props;
  const { portalKind } = useContext(AvisContext);
  const [returnPortalType] = useSessionStorage(
    AvisConstants.eId_return_to_portal_session_key,
  );
  const device = useDeviceDetection();

  useMemo(() => {
    if (device === undefined) {
      isMobileView.value = false;
      isTabletOrMobileView.value = false;

      return;
    }

    isMobileView.value = device === AvisDevice.mobile;
    isTabletOrMobileView.value = device !== AvisDevice.desktop;
  }, [device]);

  if (!portalKind) {
    //Returning from eID auth or password change - PortalType is saved in sessionStorage
    const returnToPortal: PortalType = returnPortalType as PortalType;

    //Enable location check only if there is stored return value for this "return from eID"
    if (!returnPortalType) return <NotFoundPage />;

    if (location.pathname === AvisConstants.eId_return_url_login)
      return (
        <EIdRedirectPage redirectType="login" returnToPortal={returnToPortal} />
      );

    if (location.pathname === AvisConstants.eId_return_url_change_password)
      return (
        <EIdRedirectPage
          redirectType="change-password"
          returnToPortal={returnToPortal}
        />
      );

    return <NotFoundPage />;
  }

  switch (portalKind.portalType) {
    case "authors": {
      return authors;
    }
    case "licensees":
      return licensees;
    case "blank-media":
      return blankMedia;
    case "common":
      return publicPortal;
    default:
      return <NotFoundPage />;
  }
}
